import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { API } from "@/lib/brand";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user?.is_admin) {
      navigate("/journal/admin", { replace: true });
    }
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Please enter both email and password");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/auth/password-login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err?.detail === "string" ? err.detail : "Sign-in failed");
      }
      await refresh();
      navigate("/journal/admin", { replace: true });
    } catch (err) {
      toast.error(err.message || "Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-ink-soft">
        <Loader2 size={22} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-24">
      <div className="eyebrow mb-3">Journal · Admin</div>
      <h1 className="font-display text-3xl text-ink leading-tight">
        Sign in to write
      </h1>
      <p className="text-ink-soft text-[15px] leading-relaxed mt-4">
        Only allow-listed accounts can access the Journal editor.
        Readers do not need an account.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
        <label className="block">
          <span className="eyebrow block mb-2">Email</span>
          <input
            type="email"
            className="fld"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            data-testid="login-email"
            required
          />
        </label>
        <label className="block">
          <span className="eyebrow block mb-2">Password</span>
          <input
            type="password"
            className="fld"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            data-testid="login-password"
            required
          />
        </label>
        <button
          type="submit"
          disabled={submitting}
          className="btn-brass w-full justify-center disabled:opacity-60"
          data-testid="login-submit"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
          Sign in
        </button>
      </form>

      {user && !user.is_admin && (
        <p className="mt-6 text-[13px] text-brass-deep">
          Signed in as {user.email} — but this account isn't on the allowlist.
        </p>
      )}

      <div className="mt-10 text-[13px] text-ink-soft">
        <Link to="/journal" className="link-brass">← Back to the Journal</Link>
      </div>
    </div>
  );
}
